import { useEffect, useMemo, useRef, useState } from "react";
import type { Column } from "@tanstack/react-table";

const DEFAULT_DEBOUNCE_MS = 1000;

export default function ColumnFilter<D>({ column }: { column: Column<D, unknown> }) {
    const filterValue = column.getFilterValue() as string | undefined;
    const setFilter = column.setFilterValue;

    // Per-column config rides along on the column meta because the table only
    // ever renders us as <Filter column={column} /> - there is no render-site
    // prop channel. `filterDebounceMs: 0` opts a column into filter-as-you-type.
    const { filterDebounceMs, filterPlaceholder } = column.columnDef.meta ?? {};
    const debounceMs = filterDebounceMs ?? DEFAULT_DEBOUNCE_MS;

    const [value, setValue] = useState(filterValue);

    // Keep the latest setFilter and debounce window in refs so the memoized
    // pusher never captures a stale closure without being re-created per render.
    const setFilterRef = useRef(setFilter);
    setFilterRef.current = setFilter;
    const debounceRef = useRef(debounceMs);
    debounceRef.current = debounceMs;
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Debounce filter updates so we only push to the table after the user stops
    // typing. Under react-table v7 this used to call useAsyncDebounce, but that
    // helper was compiled against a global `regeneratorRuntime` that Vite/esbuild
    // never provides, so invoking it threw "regeneratorRuntime is not defined". A
    // plain setTimeout debounce has identical behavior and no such dependency.
    // A debounce of 0 skips the timer and pushes on the same tick as the
    // keystroke, which is how the compliance table filters as you type.
    const pushFilter = useMemo(
        () => (next: string) => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            // Empty string is normalized to undefined so react-table drops the
            // filter rather than keeping an active "matches everything" one.
            const normalized = next || undefined;
            if (debounceRef.current <= 0) {
                setFilterRef.current(normalized);
                return;
            }
            timeoutRef.current = setTimeout(() => {
                setFilterRef.current(normalized);
            }, debounceRef.current);
        },
        []
    );

    useEffect(
        () => () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        },
        []
    );

    return (
        <span>
            <input
                type="text"
                className="rounded border border-gray-400 h-6"
                placeholder={filterPlaceholder}
                value={value || ""}
                onChange={(e) => {
                    setValue(e.target.value);
                    pushFilter(e.target.value);
                }}
            />
        </span>
    );
}
